// Market data for the Invest tab.
//
// Stock and ETF prices now come from the `market-data` Supabase Edge Function. That keeps the provider key out of
// the browser bundle and lets every user share one server-side cache (see supabase/functions/market-data).
// Crypto still goes straight to CoinGecko: it needs no key, and each person's own IP keeps its rate limit spread out.
//
// Every quote carries the currency it was quoted in. The old code assumed USD; see src/invest/fx.js.
//
// The last good price for everything is kept in localStorage (the "price book"), so with no connection the app
// still shows real numbers, marked with how old they are.
import { supabase, syncConfigured } from "./supabaseClient.js";
import { normalizeQuote } from "./invest/fx.js";
import { marketOpen } from "../supabase/functions/_shared/market.ts";

const PREVIEW = typeof __PREVIEW__ !== "undefined" && __PREVIEW__;
const BOOK = "bb-price-book", FXK = "bb-fx", HIST = "bb-hist:";

// Stocks need the Supabase function, i.e. a configured project. No account is needed: signed-out visitors are
// metered per IP address on the server (test phase).
export const marketDataConfigured = syncConfigured || PREVIEW;
export const marketOpenFor = (symbol) => marketOpen(symbol || "AAPL");
export const isMarketOpen = () => marketOpen("AAPL");

const read = (k) => { try { return JSON.parse(localStorage.getItem(k) || "null"); } catch (e) { return null; } };
const write = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* storage full or blocked: carry on without a cache */ } };

export const keyOf = (x) => `${x.assetType === "crypto" ? "crypto" : "stock"}:${String(x.symbol).toUpperCase()}`;
export const loadPriceBook = () => read(BOOK) || {};

const COINGECKO_IDS = { BTC: "bitcoin", ETH: "ethereum", SOL: "solana", ADA: "cardano", XRP: "ripple", DOGE: "dogecoin", BNB: "binancecoin", MATIC: "matic-network", USDT: "tether", USDC: "usd-coin", LTC: "litecoin", DOT: "polkadot", AVAX: "avalanche-2", LINK: "chainlink", TRX: "tron" };

async function proxy(body) {
  if (!syncConfigured) throw Object.assign(new Error("Stock prices need the market-data service, which isn't set up yet."), { code: "nosync" });
  const { data, error } = await supabase.functions.invoke("market-data", { body });
  if (error) {
    let msg = error.message || "Price service unavailable", status = error.context?.status;
    // our own errors use "error"; the Supabase gateway uses "message"
    try { const j = await error.context?.json?.(); if (j?.error || j?.message) msg = j.error || j.message; } catch (e) { /* keep default */ }
    throw Object.assign(new Error(msg), { code: status === 429 ? "limit" : "net" });
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

function tidy(q) {
  const n = normalizeQuote(q.price, q.currency);
  const factor = n.price / q.price || 1;
  return { ...q, price: n.price, currency: n.currency, prevClose: q.prevClose != null ? q.prevClose * factor : null };
}

async function geckoQuotes(items) {
  const ids = {};
  items.forEach((h) => { const id = h.cgId || COINGECKO_IDS[String(h.symbol).toUpperCase()]; if (id) ids[id] = h; });
  const out = {};
  if (!Object.keys(ids).length) return out;
  const res = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${Object.keys(ids).join(",")}&vs_currencies=usd&include_24hr_change=true`);
  const data = await res.json();
  for (const [id, h] of Object.entries(ids)) {
    const e = data?.[id];
    if (e && typeof e.usd === "number") out[keyOf(h)] = { price: e.usd, currency: "USD", changePct: e.usd_24h_change ?? null, prevClose: e.usd_24h_change != null ? e.usd / (1 + e.usd_24h_change / 100) : null, ts: Date.now(), source: "coingecko" };
  }
  return out;
}

// items: [{ symbol, assetType, cgId }] -> { quotes: { [key]: quote }, errors, status }
// status: "live" | "partial" | "offline" | "setup" (no market-data service configured) — drives the banner in the UI. Quotes missing from the network fall back to the price book.
export async function getQuotes(items) {
  if (PREVIEW) return (await import("./invest/previewServer.js")).quotes(items, loadPriceBook, (b) => write(BOOK, b));
  const book = loadPriceBook(), quotes = {}, errors = {};
  const stocks = items.filter((i) => i.assetType !== "crypto"), crypto = items.filter((i) => i.assetType === "crypto");
  let status = "live";

  if (stocks.length) {
    try {
      for (let i = 0; i < stocks.length; i += 40) {
        const chunk = stocks.slice(i, i + 40);
        const r = await proxy({ action: "quotes", items: chunk.map((c) => ({ symbol: c.symbol })) });
        for (const c of chunk) {
          const q = r.quotes?.[String(c.symbol).toUpperCase()];
          if (q) quotes[keyOf(c)] = tidy(q); else if (r.errors?.[String(c.symbol).toUpperCase()]) errors[keyOf(c)] = r.errors[String(c.symbol).toUpperCase()];
        }
      }
    } catch (e) { status = e.code === "nosync" ? "setup" : "offline"; stocks.forEach((c) => { errors[keyOf(c)] = e.message; }); }
  }
  if (crypto.length) {
    try { Object.assign(quotes, await geckoQuotes(crypto)); } catch (e) { if (status === "live") status = "offline"; }
  }
  for (const it of items) {
    const k = keyOf(it);
    if (quotes[k]) book[k] = { ...quotes[k], fetchedAt: Date.now(), stale: undefined };
    else if (book[k]) quotes[k] = { ...book[k], stale: true };
  }
  if (status === "live" && items.some((i) => !quotes[keyOf(i)] || quotes[keyOf(i)].stale)) status = "partial";
  write(BOOK, book);
  return { quotes, errors, status };
}

// -> { points: [{t, price}], currency, stale? }. Falls back to the last copy saved on this device.
export async function getHistory(item, range = "1M") {
  if (PREVIEW) return (await import("./invest/previewServer.js")).history(item, range);
  const key = HIST + keyOf(item) + ":" + range, saved = read(key);
  if (saved && Date.now() - saved.at < 3 * 3600 * 1000) return saved.data;
  try {
    let data;
    if (item.assetType === "crypto") {
      const id = item.cgId || COINGECKO_IDS[String(item.symbol).toUpperCase()];
      const days = { "1W": 7, "1M": 30, "3M": 90, "1Y": 365, "5Y": 1825 }[range] || 30;
      const res = await fetch(`https://api.coingecko.com/api/v3/coins/${id}/market_chart?vs_currency=usd&days=${days}`);
      const j = await res.json();
      data = { points: (j?.prices || []).map(([t, price]) => ({ t, price })), currency: "USD" };
    } else {
      data = await proxy({ action: "history", symbol: String(item.symbol).toUpperCase(), range });
      const c = normalizeQuote(1, data.currency); // pence-quoted series
      data = { ...data, currency: c.currency, points: data.points.map((p) => ({ ...p, price: p.price * c.price })) };
    }
    if (data.points?.length) write(key, { at: Date.now(), data });
    return data;
  } catch (e) {
    return saved ? { ...saved.data, stale: true } : { points: [], currency: null, error: e.message };
  }
}

// -> { hits: [{ id, symbol, name, exchange, currency }], note? }
export async function searchSymbols(query, assetType) {
  const q = String(query || "").trim();
  if (!q) return { hits: [] };
  if (PREVIEW) return (await import("./invest/previewServer.js")).search(q, assetType);
  if (assetType === "crypto") {
    try {
      const res = await fetch(`https://api.coingecko.com/api/v3/search?query=${encodeURIComponent(q)}`);
      const data = await res.json();
      return { hits: (data?.coins || []).slice(0, 8).map((c) => ({ id: c.id, symbol: c.symbol.toUpperCase(), name: c.name, currency: "USD" })) };
    } catch (e) { return { hits: [] }; }
  }
  try {
    const r = await proxy({ action: "search", q });
    return { hits: (r.hits || []).map((h) => ({ ...h, id: h.symbol })), note: r.note };
  } catch (e) {
    return { hits: [], note: `Stock search isn't available: ${e.message}. You can still type a ticker and add it.` };
  }
}

// Units per 1 USD for every currency. Cached on the device; used stale rather than not at all.
export async function getFxTable() {
  if (PREVIEW) return (await import("./invest/previewServer.js")).fx();
  const saved = read(FXK);
  if (saved && Date.now() - saved.ts < 6 * 3600 * 1000) return saved;
  let table = null;
  try { if (syncConfigured) { const r = await proxy({ action: "fx" }); if (r?.rates) table = { rates: r.rates, ts: Date.now() }; } } catch (e) { /* use the direct sources */ }
  if (!table) {
    const rates = { USD: 1 };
    try { const j = await (await fetch("https://open.er-api.com/v6/latest/USD")).json(); Object.assign(rates, j?.rates || {}); } catch (e) { /* next */ }
    try { const j = await (await fetch("https://api.frankfurter.app/latest?from=USD")).json(); Object.assign(rates, j?.rates || {}); } catch (e) { /* next */ }
    if (Object.keys(rates).length > 1) table = { rates, ts: Date.now() };
  }
  if (table) { write(FXK, table); return table; }
  return saved ? { ...saved, stale: true } : null;
}
